import { Component } from '@theme/component';

/**
 * Two-page announcement bar: page 1 = row of announcements, page 2 = secondary message.
 * Supports fade and vertical slide transitions, with seamless downward loop for slide mode.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} pagesTrack
 * @property {HTMLElement[]} [pages]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {HTMLElement | null} */
  #clonePage = null;

  /** @type {boolean} */
  #isJumping = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize);
    this.refs.pagesTrack?.addEventListener('transitionend', this.#handleTransitionEnd);

    // Lock viewport to the active page while inactive pages are still display:none.
    this.#updateViewportHeight();
    this.#setupSlideLoop();
    this.#applyPage(this.#current, { instant: true });

    // Enable fade/slide layout; overflow + fixed height keep page 2 clipped (no FOUC).
    this.setAttribute('data-ready', '');
    this.#applyPage(this.#current, { instant: true });

    if (this.autoplay && this.#rotatablePageCount() > 1) {
      this.play();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
    this.refs.pagesTrack?.removeEventListener('transitionend', this.#handleTransitionEnd);
    this.#clonePage?.remove();
    this.#clonePage = null;
    this.suspend();
    super.disconnectedCallback();
  }

  next() {
    if (!this.autoplay || this.#rotatablePageCount() < 2) return;

    // If we landed on the seamless clone but transitionend did not fire (e.g. reduced motion), reset first.
    const realCount = this.#rotatablePageCount();
    if (this.#clonePage && this.#current >= realCount) {
      this.#applyPage(0, { instant: true });
    }

    this.#goTo(this.#current + 1);
  }

  previous() {
    if (!this.autoplay || this.#rotatablePageCount() < 2) return;
    this.#goTo(this.#current - 1);
  }

  /**
   * Starts automatic page playback.
   * @param {number} [interval] - Interval in milliseconds.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#rotatablePageCount() < 2) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused || this.#rotatablePageCount() < 2) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value) || value <= 0) return undefined;

    return value * 1000;
  }

  get transition() {
    return this.dataset.transition === 'fade' ? 'fade' : 'slide_vertical';
  }

  #isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** Real pages from Liquid (excludes seamless-loop clone). */
  #realPages() {
    return (this.refs.pages ?? []).filter((page) => !page.hasAttribute('data-clone'));
  }

  #visiblePages() {
    const pages = this.#realPages();
    if (!this.#isMobileViewport()) return pages;
    return pages.filter((page) => !page.classList.contains('announcement-bar__page--hide-mobile'));
  }

  #rotatablePageCount() {
    return this.#visiblePages().length;
  }

  #setupSlideLoop() {
    this.#clonePage?.remove();
    this.#clonePage = null;

    if (this.transition !== 'slide_vertical') return;

    const pages = this.#visiblePages();
    if (pages.length < 2 || !this.refs.pagesTrack) return;

    const first = pages[0];
    const clone = /** @type {HTMLElement} */ (first.cloneNode(true));
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.pagesTrack.appendChild(clone);
    this.#clonePage = clone;
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  #goTo(index, options = {}) {
    this.#applyPage(index, options);
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  #applyPage(index, { instant = false } = {}) {
    const visible = this.#visiblePages();
    if (visible.length === 0) return;

    if (this.transition === 'fade') {
      const relativeIndex = ((index % visible.length) + visible.length) % visible.length;
      this.#current = relativeIndex;

      this.#realPages().forEach((page) => {
        const isActive = page === visible[relativeIndex];
        page.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.#updateViewportHeight();
      return;
    }

    // slide_vertical
    const realCount = visible.length;
    let target = index;

    if (this.#clonePage && target === realCount) {
      // Advance onto the clone of page 0 (seamless downward loop).
      this.#current = target;
    } else {
      target = ((index % realCount) + realCount) % realCount;
      this.#current = target;
    }

    this.#realPages().forEach((page) => {
      const pageIndex = Number(page.dataset.page);
      const isActive = pageIndex === (this.#current % realCount);
      page.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    this.#clonePage?.setAttribute('aria-hidden', this.#current === realCount ? 'false' : 'true');

    if (instant) {
      this.classList.add('announcement-bar__slider--no-transition');
    }

    this.#updateViewportHeight();
    this.#updateSlideOffset();

    if (instant) {
      // Force reflow then re-enable transitions.
      void this.offsetHeight;
      this.classList.remove('announcement-bar__slider--no-transition');
    }
  }

  #updateViewportHeight() {
    const visible = this.#visiblePages();
    if (visible.length === 0) return;

    const activeIndex = this.#current % visible.length;
    const activePage = visible[activeIndex] ?? visible[0];
    const height = Math.ceil(activePage.getBoundingClientRect().height);

    if (height > 0) {
      this.style.setProperty('--announcement-viewport-height', `${height}px`);
    }

    // Equalize page heights in slide mode so translate distances stay even.
    if (this.transition === 'slide_vertical') {
      const pages = [...visible];
      if (this.#clonePage) pages.push(this.#clonePage);
      pages.forEach((page) => {
        page.style.minHeight = `${height}px`;
      });
    }
  }

  #updateSlideOffset() {
    if (this.transition !== 'slide_vertical') return;

    const visible = this.#visiblePages();
    if (visible.length === 0) return;

    const viewportHeight =
      parseFloat(this.style.getPropertyValue('--announcement-viewport-height')) ||
      visible[0].getBoundingClientRect().height;

    const offset = this.#current * viewportHeight;
    this.style.setProperty('--announcement-page-offset', `${offset}px`);
  }

  #handleTransitionEnd = (event) => {
    if (event.target !== this.refs.pagesTrack) return;
    if (event.propertyName !== 'transform') return;
    if (this.transition !== 'slide_vertical' || !this.#clonePage) return;
    if (this.#isJumping) return;

    const realCount = this.#visiblePages().length;
    if (this.#current !== realCount) return;

    // Finished animating onto clone of page 0 — jump back to real page 0.
    this.#isJumping = true;
    this.#applyPage(0, { instant: true });
    this.#isJumping = false;
  };

  #handleResize = () => {
    this.#setupSlideLoop();
    const visible = this.#visiblePages();
    if (visible.length === 0) return;

    const normalized = this.#current % Math.max(visible.length, 1);
    this.#applyPage(normalized, { instant: true });

    if (this.autoplay && visible.length > 1 && !this.paused && !this.#interval) {
      this.play();
    } else if (visible.length < 2) {
      this.suspend();
    }
  };

  /**
   * Suspend autoplay while the tab is hidden; resume when visible again.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
      return;
    }

    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
